import React, { useCallback, useEffect, useMemo, useState } from 'react';
import authAxios from '../utils/authAxios';
import StandardTableLayout from '../styles/StandardTableLayout';
import { SizeTag } from '../styles/CustomerSpecsIndexStyles';
import SecureImage from '../components/SecureImage';
import CaseTagModal, { CASETAG_FOLDER, toFilename } from '../components/CaseTagModal';
import CaseTagReplaceDialog from '../components/CaseTagReplaceDialog';
import { LightboxBackdrop, LightboxCloseButton } from '../styles/PackageIndexStyles';
import { revokeCachedUrl } from '../utils/imageCache';
import { caseTagScopeLabels } from '../utils/caseTagScope';

const columns = [
  { key: 'ImageLink', label: 'Image' },
  { key: 'CaseTagName', label: 'Case Tag Name' },
  { key: 'Customers', label: 'Customers' },
];

// Image has no meaningful order; every other column sorts on its displayed text.
const SORTABLE = new Set(['CaseTagName', 'Customers']);

// Landscape preview so case tag artwork is legible without opening the lightbox;
// `contain` keeps the uploaded aspect ratio instead of cropping it to a square
// (matches BagTraceabilityPage).
const thumbStyle = {
  width: '168px',
  height: '92px',
  objectFit: 'contain',
  background: 'var(--white)',
  borderRadius: '6px',
  border: '1px solid var(--shadow-a08)',
  cursor: 'pointer',
  display: 'block',
};

const PlusButton = ({ label, ariaLabel, onClick, tone }) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    style={{
      height: 26, minWidth: 26, padding: '0 8px', borderRadius: 13, flex: '0 0 auto',
      background: tone, color: 'var(--white)', border: 'none',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 800, cursor: 'pointer',
    }}
  >
    {label}
  </button>
);

// Pill list that shows the first 4 values then a "+N" expander (a "−" collapses).
// Local to the page, mirroring the Bag Traceability CollapsedPills.
const CollapsedPills = ({ items, name, expanded, onExpand, onCollapse }) => {
  const maxShown = 4;
  const visible = expanded ? items : items.slice(0, maxShown);
  const remaining = Math.max(0, items.length - maxShown);
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      {visible.map((v, i) => (
        <SizeTag key={`${i}-${v}`} style={{ background: 'var(--grey-600)' }}>{v}</SizeTag>
      ))}
      {remaining > 0 && !expanded ? (
        <PlusButton label={`+${remaining}`} ariaLabel={`Show all ${name}`} onClick={onExpand} tone="var(--green-800)" />
      ) : null}
      {expanded && items.length > maxShown ? (
        <PlusButton label="−" ariaLabel={`Show fewer ${name}`} onClick={onCollapse} tone="var(--grey-700)" />
      ) : null}
    </div>
  );
};

const CaseTagsPage = () => {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState(null); // { key, direction: 'asc'|'desc' }
  const [modal, setModal] = useState({ open: false, mode: 'add', row: null });
  const [replaceDialog, setReplaceDialog] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  // Per-cell "+N" expansion, keyed `${row.UniqueID}:${colKey}`.
  const [pillsExpanded, setPillsExpanded] = useState(() => new Set());
  // Bumped after any image change so SecureImage thumbnails remount and re-probe.
  const [imgVersion, setImgVersion] = useState(0);

  const collator = useMemo(() => new Intl.Collator(undefined, { sensitivity: 'base' }), []);

  const fetchRows = useCallback(async () => {
    try {
      const res = await authAxios.get('/api/casetags');
      setRows(res.data);
    } catch (e) { console.error('Fetch Case Tags failed', e); }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await authAxios.get('/api/customers/dropdown');
      setCustomers(res.data);
    } catch (e) { console.error('Fetch Customers failed', e); }
  }, []);

  useEffect(() => { fetchRows(); fetchCustomers(); }, [fetchRows, fetchCustomers]);

  // Close the lightbox on Escape.
  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightboxSrc(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxSrc]);

  const customerNameById = useMemo(() => {
    const m = new Map();
    for (const c of customers) m.set(String(c.UniqueID), String(c.Customer || ''));
    return m;
  }, [customers]);

  // Drop any cached blob object URL for a changed image so the SAS path doesn't serve stale bytes.
  const evictCache = (link) => {
    const f = toFilename(link);
    if (f) revokeCachedUrl(`${CASETAG_FOLDER}/${f}`);
  };

  const openAdd = () => setModal({ open: true, mode: 'add', row: null });
  const openEdit = (row) => setModal({ open: true, mode: 'edit', row });
  const closeModal = () => setModal((m) => ({ ...m, open: false }));

  const handleSaved = async ({ oldImageLink, newImageLink }) => {
    evictCache(oldImageLink);
    evictCache(newImageLink);
    setImgVersion((v) => v + 1);
    closeModal();
    await fetchRows();
  };

  // Block-and-Inform delete: pre-check dependents, only DELETE when nothing references the tag.
  const requestDelete = async (row) => {
    if (busy || !row) return;
    closeModal();
    setBusy(true);
    try {
      const { data: deps } = await authAxios.get(`/api/casetags/${row.UniqueID}/dependents`);
      if ((deps?.specs?.count || 0) > 0) {
        setReplaceDialog({ open: true, row, dependents: deps });
        return;
      }
      if (!window.confirm(`Delete case tag "${row.CaseTagName}"?`)) return;
      await authAxios.delete(`/api/casetags/${row.UniqueID}`);
      evictCache(row.ImageLink);
      setImgVersion((v) => v + 1);
      fetchRows();
    } catch (e) {
      console.error('Case tag delete flow failed', e);
      alert('Could not delete case tag.');
    } finally {
      setBusy(false);
    }
  };

  // Delete a tag that has dependents: reassign them to `replaceWith` (a tag id, or null for
  // "No case tag"), then delete — one transactional backend call.
  const confirmReplaceDelete = async (replaceWith) => {
    const row = replaceDialog?.row;
    if (!row) return;
    setBusy(true);
    try {
      await authAxios.post(`/api/casetags/${row.UniqueID}/replace-delete`, { replaceWith });
      evictCache(row.ImageLink);
      setImgVersion((v) => v + 1);
      setReplaceDialog(null);
      fetchRows();
    } catch (e) {
      console.error('Case tag replace-delete failed', e);
      alert('Could not delete case tag.');
    } finally {
      setBusy(false);
    }
  };

  // What each sortable/searchable column actually shows, so sorting and search both work
  // on resolved customer names and the "All Customers" sentinel — not on raw CSV ids.
  const cellText = useCallback((row) => ({
    CaseTagName: String(row?.CaseTagName ?? ''),
    Customers: caseTagScopeLabels(row, customerNameById).join(', '),
  }), [customerNameById]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => Object.values(cellText(r)).some((v) => v.toLowerCase().includes(q)));
  }, [rows, searchQuery, cellText]);

  // No sort selected = the default Case Tag Name order.
  const sorted = useMemo(() => {
    const copy = [...filtered];
    if (!sortConfig) {
      return copy.sort((a, b) => collator.compare(a.CaseTagName ?? '', b.CaseTagName ?? ''));
    }
    const { key, direction } = sortConfig;
    return copy.sort((a, b) => {
      const cmp = collator.compare(cellText(a)[key] || '', cellText(b)[key] || '')
        || collator.compare(a.CaseTagName ?? '', b.CaseTagName ?? '');
      return direction === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortConfig, collator, cellText]);

  // A-Z -> Z-A -> back to the default order.
  const handleSort = (key) => {
    if (!SORTABLE.has(key)) return;
    setSortConfig((prev) => {
      if (prev?.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  };

  const isExpanded = (row, colKey) => pillsExpanded.has(`${row.UniqueID}:${colKey}`);
  const expand = (row, colKey) => setPillsExpanded((prev) => {
    const n = new Set(prev); n.add(`${row.UniqueID}:${colKey}`); return n;
  });
  const collapse = (row, colKey) => setPillsExpanded((prev) => {
    const n = new Set(prev); n.delete(`${row.UniqueID}:${colKey}`); return n;
  });

  // Rows open the editor (Delete lives inside it). Inner controls — the image and the
  // "+N" chips — stop propagation so they still work on their own.
  const rowKeyDown = (e, row) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (e.target !== e.currentTarget) return;
    e.preventDefault();
    openEdit(row);
  };

  return (
    <>
      <StandardTableLayout
        title="Case Tags"
        showForm={false}
        submitting={busy}
        onToggleForm={openAdd}
        onSubmit={(e) => e.preventDefault()}
        columns={columns}
        formData={{}}
        handleChange={() => {}}
        rows={sorted}
        editingId={null}
        hideActionsColumn
        sortConfig={sortConfig}
        onSort={handleSort}
        searchValue={searchQuery}
        onSearchChange={(e) => setSearchQuery(e.target.value)}
        getRowProps={(row, index) => ({
          onClick: () => openEdit(row),
          onKeyDown: (e) => rowKeyDown(e, row),
          tabIndex: 0,
          'aria-label': `Edit ${row?.CaseTagName || 'case tag'}`,
          style: { cursor: 'pointer' },
          $index: index,
        })}
        renderCell={(col, row) => {
          if (col === 'ImageLink') {
            const filename = toFilename(row?.ImageLink);
            if (!filename) return <span style={{ color: 'var(--ink-a45)' }}>—</span>;
            return (
              <SecureImage
                key={`thumb-${row.UniqueID}-${imgVersion}`}
                src={filename}
                folder={CASETAG_FOLDER}
                alt={row.CaseTagName}
                style={thumbStyle}
                title="Click to enlarge"
                onClick={(e) => { e.stopPropagation(); setLightboxSrc(filename); }}
              />
            );
          }
          if (col === 'CaseTagName') return row?.CaseTagName ?? '';
          if (col === 'Customers') {
            const labels = caseTagScopeLabels(row, customerNameById);
            if (labels.length === 0) return <span style={{ color: 'var(--ink-a45)' }}>—</span>;
            return (
              <CollapsedPills
                items={labels}
                name="customers"
                expanded={isExpanded(row, col)}
                onExpand={() => expand(row, col)}
                onCollapse={() => collapse(row, col)}
              />
            );
          }
          return null;
        }}
      />

      <CaseTagModal
        open={modal.open}
        mode={modal.mode}
        row={modal.row}
        customers={customers}
        imgVersion={imgVersion}
        busy={busy}
        onClose={closeModal}
        onSaved={handleSaved}
        onRequestDelete={requestDelete}
      />

      <CaseTagReplaceDialog
        open={Boolean(replaceDialog?.open)}
        tag={replaceDialog?.row}
        dependents={replaceDialog?.dependents}
        caseTags={rows}
        customers={customers}
        busy={busy}
        onCancel={() => setReplaceDialog(null)}
        onConfirm={confirmReplaceDelete}
      />

      {lightboxSrc && (
        <LightboxBackdrop onClick={() => setLightboxSrc(null)} role="dialog" aria-modal="true">
          <LightboxCloseButton
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxSrc(null); }}
            aria-label="Close image"
          >
            ×
          </LightboxCloseButton>
          <div onClick={(e) => e.stopPropagation()}>
            <SecureImage
              key={`lightbox-${lightboxSrc}-${imgVersion}`}
              src={lightboxSrc}
              folder={CASETAG_FOLDER}
              alt="Case tag image"
              style={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 12 }}
            />
          </div>
        </LightboxBackdrop>
      )}
    </>
  );
};

export default CaseTagsPage;
